/**
 * /ice-thickness — page controller. The model and its measured error live in
 * model.js; the weather record and the place in outdoor-weather/*.
 *
 * ⛔ The page never calls ice «safe» in any language: it prints a range, the
 * official thresholds next to it, and what the model cannot see.
 */
import { simulate, band, CALIBRATION } from './model.js';
import { CLASSES } from '../outdoor-weather/water.js';
import { fetchConditions, forecastHours, dailyAir, localDay } from '../outdoor-weather/series.js';
import { fromUrl, saved, fromProfile, fillTz, remember, mountPicker, geolocate, offsetHours } from '../outdoor-weather/place.js';

const SLUG = 'ice-thickness';
const CFG = window.__IT || { T: {}, li: {}, cfg: {} };
const T = CFG.T || {};
const DAY = 86400000;

const $ = (s) => document.querySelector(s);
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
function t(key, vars) {
    let s = T[key] != null ? String(T[key]) : key;
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(':' + k).join(String(v));
    return s;
}
const locale = () => (window.__intlLocale ? window.__intlLocale() : (CFG.cfg.locale || 'en'));
const nf = (d) => new Intl.NumberFormat(locale(), { maximumFractionDigits: d, minimumFractionDigits: d });

/**
 * Official thresholds. RU: МЧС России («Правила поведения на льду»). Elsewhere
 * the widely reprinted Minnesota DNR guidance for new clear ice (4/5/8/12 in).
 * Both are quoted with their source; neither is turned into «safe».
 */
const THRESHOLDS = CFG.cfg.locale === 'ru'
    ? { src: 'thr_src_ru', rows: [[7, 'thr_ru_one'], [12, 'thr_ru_rink'], [15, 'thr_ru_group'], [30, 'thr_ru_car']] }
    : { src: 'thr_src_intl', rows: [[10.2, 'thr_in_walk'], [12.7, 'thr_in_atv'], [20.3, 'thr_in_car'], [30.5, 'thr_in_truck']] };

const state = { loc: null, water: 'lake', anchor: null, unit: 'cm', data: null, derived: null, loading: false };

/* ── preferences ───────────────────────────────────────────────────────── */
function loadPrefs() {
    let p = null;
    try { p = window.__toolSettings && window.__toolSettings.load(SLUG, 'prefs', null); } catch (_) { /* none */ }
    const imperial = (() => { try { return window.__toolSettings && window.__toolSettings.units() === 'imperial'; } catch (_) { return false; } })();
    state.water = p && CLASSES[p.water] ? p.water : 'lake';
    state.unit = p && (p.unit === 'in' || p.unit === 'cm') ? p.unit : (imperial ? 'in' : 'cm');
    state.anchor = p && p.anchor && /^\d{4}-\d{2}-\d{2}$/.test(p.anchor.d) && Number.isFinite(p.anchor.cm) ? p.anchor : null;
}
function savePrefs() {
    try { window.__toolSettings.save(SLUG, 'prefs', { water: state.water, unit: state.unit, anchor: state.anchor }); } catch (_) { /* private mode */ }
}

const fmtLen = (cm, d = 0) => (!Number.isFinite(cm) ? '—'
    : state.unit === 'in' ? nf(d ? 1 : 0).format(cm / 2.54) + ' ' + t('u_in') : nf(d).format(cm) + ' ' + t('u_cm'));
function fmtDate(iso) {
    return new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(iso + 'T12:00:00Z'));
}

function status(kind, text) {
    const el = $('#itStatus');
    el.hidden = !kind;
    el.className = 'tc-msg it-status' + (kind === 'error' ? ' bad' : '') + (kind === 'loading' ? ' it-loading' : '');
    el.textContent = text || '';
}
function setPlaceLine() {
    $('#itPlace').textContent = state.loc ? (state.loc.name || nf(2).format(state.loc.lat) + ', ' + nf(2).format(state.loc.lng)) : t('place_none');
}
function togglePicker(open) {
    const w = $('#itPickerWrap');
    w.hidden = !open;
    $('#itChange').setAttribute('aria-expanded', String(open));
    if (open && !w.dataset.mounted) {
        w.dataset.mounted = mountPicker($('#itPicker'), SLUG, CFG.li, state.loc, (loc) => { togglePicker(false); load(loc); }) ? '1' : '';
    }
}

/* ── load + compute ────────────────────────────────────────────────────── */
async function load(loc) {
    if (state.loading) return;
    state.loading = true;
    state.loc = await fillTz(loc);
    setPlaceLine();
    status('loading', t('loading'));
    $('#itBody').hidden = true;
    if (window.__stage) window.__stage('fetch');
    try {
        const data = await fetchConditions(state.loc.lat, state.loc.lng);
        const hasHist = data.cond && data.cond.daily && Array.isArray(data.cond.daily.t) && data.cond.daily.t.length > 20;
        if (!hasHist) throw new Error('history:' + (data.cond && data.cond.err));
        state.data = data;
        recompute();
        status(null);
        $('#itBody').hidden = false;
        render();
        remember(SLUG, state.loc);
        if (window.__announce) window.__announce(t('sr_loaded', { place: state.loc.name || '' }));
        if (window.__stage) window.__stage('shown');
        if (window.__aeToolComplete) {
            const s = state.derived.today;
            window.__aeToolComplete({ frozen: s && s.frozen ? 1 : 0, anchored: state.derived.anchored ? 1 : 0, fc: data.fc && !data.fc.err ? 1 : 0 });
        }
    } catch (e) {
        status('error', t('err_load'));
        if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'fetch_failed', detail: String(e && e.message || e).slice(0, 120) });
    } finally {
        state.loading = false;
    }
}

function recompute() {
    const { cond, fc } = state.data;
    const offsetH = offsetHours(state.loc.tz, state.loc.lng);
    const fcRows = fc && !fc.err ? forecastHours(fc) : [];
    const days = dailyAir(cond, fcRows, offsetH);
    const todayKey = localDay(Date.now(), offsetH);
    let ti = days.findIndex((d) => d.d === todayKey);
    if (ti < 0) ti = days.length - 1;
    let anchor;
    if (state.anchor) {
        const ai = days.findIndex((d) => d.d === state.anchor.d);
        if (ai >= 0 && ai <= ti) anchor = { i: ai, hCm: state.anchor.cm };
    }
    const sim = simulate(days, CLASSES[state.water], { anchor });
    // Freeze-up of the current ice: walk back from today while frozen.
    let fu = -1;
    if (sim[ti] && sim[ti].frozen) {
        fu = ti;
        while (fu > 0 && sim[fu - 1].frozen) fu--;
    }
    // Ice expected within the forecast, if none yet.
    let nextFreeze = -1;
    if (!(sim[ti] && sim[ti].frozen)) {
        for (let i = ti + 1; i < sim.length; i++) if (sim[i].frozen) { nextFreeze = i; break; }
    }
    const anchored = anchor && ti - anchor.i <= 10 ? anchor : null;
    state.derived = { offsetH, days, sim, ti, today: sim[ti], fu, nextFreeze, anchored, anchorAny: anchor || null };
}

/** Band for a day: the anchored error right after a measurement, else the calibration band. */
function bandAt(i) {
    const D = state.derived;
    const s = D.sim[i];
    if (!s || !s.frozen) return { lo: 0, hi: 0 };
    if (D.anchorAny && i >= D.anchorAny.i && i - D.anchorAny.i <= 10) {
        const e = CALIBRATION.anchoredP80Cm / 100;
        return { lo: Math.max(0, s.hi - e), hi: s.hi + e };
    }
    return band(s.hi);
}

/* ── render ────────────────────────────────────────────────────────────── */
function render() {
    renderAnswer();
    renderStats();
    renderChart();
    renderNotes();
}

function renderAnswer() {
    const D = state.derived, s = D.today;
    const box = $('#itAnswer');
    if (!s) { box.innerHTML = ''; return; }
    let html;
    if (!s.frozen) {
        const lines = ['<p class="tc-answer-l">' + esc(t('ans_label')) + '</p>',
            '<p class="tc-answer-v muted">' + esc(t('ans_open')) + '</p>'];
        let sub = t('ans_open_water', { t: fmtWaterTemp(s.tw) });
        if (D.nextFreeze >= 0) sub += ' ' + t('ans_freeze_soon', { date: fmtDate(D.days[D.nextFreeze].d) });
        lines.push('<p class="tc-answer-sub">' + esc(sub) + '</p>');
        html = lines.join('');
    } else {
        const cm = s.hi * 100;
        const b = bandAt(D.ti);
        const lo = b.lo * 100, hi = b.hi * 100;
        const below = THRESHOLDS.rows.filter(([c]) => lo < c);
        const first = THRESHOLDS.rows[0];
        let verdict;
        if (lo < first[0]) verdict = t('verdict_below', { lo: fmtLen(lo), thr: fmtLen(first[0]), what: t(first[1]), src: t(THRESHOLDS.src) });
        else if (below.length) verdict = t('verdict_between', { lo: fmtLen(lo), thr: fmtLen(below[0][0]), what: t(below[0][1]), src: t(THRESHOLDS.src) });
        else verdict = t('verdict_above', { lo: fmtLen(lo) });
        html = '<p class="tc-answer-l">' + esc(t('ans_label')) + '</p>'
            + '<p class="tc-answer-v">' + esc(t('ans_about', { v: fmtLen(cm) })) + '</p>'
            + '<p class="tc-answer-sub">' + esc(t(D.anchored ? 'ans_range_anchor' : 'ans_range', { lo: fmtLen(lo), hi: fmtLen(hi) })) + '</p>'
            + '<p class="tc-msg ' + (lo < first[0] ? 'bad' : 'warn') + '">' + esc(verdict) + ' ' + esc(t('check_auger')) + '</p>';
    }
    box.innerHTML = html;
}

function fmtWaterTemp(c) {
    const imperial = state.unit === 'in';
    return imperial ? nf(0).format(c * 9 / 5 + 32) + ' °F' : nf(1).format(c) + ' °C';
}

function renderStats() {
    const D = state.derived, s = D.today;
    const tiles = [];
    if (D.fu >= 0) tiles.push([t('st_freeze'), fmtDate(D.days[D.fu].d), t('st_freeze_days', { n: D.ti - D.fu })]);
    else if (D.nextFreeze >= 0) tiles.push([t('st_freeze'), fmtDate(D.days[D.nextFreeze].d), t('st_freeze_fc')]);
    else tiles.push([t('st_freeze'), '—', t('st_freeze_none')]);
    if (s && s.frozen) {
        tiles.push([t('st_snow'), fmtLen(s.hs * 100), s.hsi > 0.005 ? t('st_white', { v: fmtLen(s.hsi * 100) }) : t('st_snow_sub')]);
        tiles.push([t('st_thaw'), nf(0).format(s.thaw) + ' ' + t('u_degday'), t('st_thaw_sub')]);
    } else if (s) {
        tiles.push([t('st_water'), fmtWaterTemp(s.tw), t('st_water_sub')]);
    }
    const last = D.sim.length - 1;
    if (last > D.ti && D.sim[last].frozen) {
        tiles.push([t('st_week'), fmtLen(D.sim[last].hi * 100), fmtDate(D.days[last].d)]);
    }
    $('#itStats').innerHTML = tiles.map(([l, v, sub]) => '<div class="tc-stat"><div class="tc-stat-l">' + esc(l) + '</div><div class="tc-stat-v">' + esc(v) + '</div>'
        + (sub ? '<div class="tc-stat-s">' + esc(sub) + '</div>' : '') + '</div>').join('');
}

function renderChart() {
    const D = state.derived;
    // From 10 days before the current ice (or before today if open) to the forecast end.
    let from = D.fu >= 0 ? Math.max(0, D.fu - 10) : Math.max(0, D.ti - 30);
    if (D.nextFreeze >= 0) from = Math.max(0, D.ti - 20);
    const to = D.sim.length - 1;
    if (to - from < 5) { $('#itChart').innerHTML = ''; return; }
    // Drawn at the container's own width so 12 px text stays 12 px on a phone.
    const W = Math.max(300, Math.round($('#itChart').clientWidth || 720)), H = 240, L = 40, R = 12, TOP = 14, B = 26;
    let maxCm = 10;
    for (let i = from; i <= to; i++) maxCm = Math.max(maxCm, bandAt(i).hi * 100);
    for (const [c] of THRESHOLDS.rows) if (c < maxCm * 1.3) maxCm = Math.max(maxCm, c + 2);
    maxCm = Math.ceil(maxCm / 10) * 10;
    const xs = (i) => L + (i - from) / (to - from) * (W - L - R);
    const ys = (cm) => TOP + (1 - cm / maxCm) * (H - TOP - B);
    const parts = [];
    // Forecast region.
    if (D.ti < to) parts.push('<rect class="it-fc" x="' + xs(D.ti).toFixed(1) + '" y="' + TOP + '" width="' + (xs(to) - xs(D.ti)).toFixed(1) + '" height="' + (H - TOP - B) + '"/>');
    for (let g = 0; g <= maxCm; g += maxCm <= 40 ? 10 : 20) {
        parts.push('<line class="it-grid" x1="' + L + '" x2="' + (W - R) + '" y1="' + ys(g).toFixed(1) + '" y2="' + ys(g).toFixed(1) + '"/>');
        parts.push('<text class="it-yl" x="' + (L - 6) + '" y="' + (ys(g) + 4).toFixed(1) + '">' + esc(state.unit === 'in' ? nf(0).format(g / 2.54) : g) + '</text>');
    }
    // Thresholds.
    for (const [c, k] of THRESHOLDS.rows) {
        if (c > maxCm) continue;
        parts.push('<line class="it-thr" x1="' + L + '" x2="' + (W - R) + '" y1="' + ys(c).toFixed(1) + '" y2="' + ys(c).toFixed(1) + '"><title>' + esc(t(k) + ' — ' + fmtLen(c)) + '</title></line>');
    }
    // Band + line.
    const top = [], bot = [], mid = [];
    for (let i = from; i <= to; i++) {
        const s = D.sim[i];
        const b = bandAt(i);
        top.push(xs(i).toFixed(1) + ' ' + ys(b.hi * 100).toFixed(1));
        bot.unshift(xs(i).toFixed(1) + ' ' + ys(b.lo * 100).toFixed(1));
        mid.push((i === from ? 'M' : 'L') + xs(i).toFixed(1) + ' ' + ys(s.frozen ? s.hi * 100 : 0).toFixed(1));
    }
    parts.push('<path class="it-band" d="M' + top.join(' L') + ' L' + bot.join(' L') + ' Z"/>');
    parts.push('<path class="it-line" d="' + mid.join(' ') + '"/>');
    // Today and the measurement.
    parts.push('<line class="it-today" x1="' + xs(D.ti).toFixed(1) + '" x2="' + xs(D.ti).toFixed(1) + '" y1="' + TOP + '" y2="' + (H - B) + '"/>');
    if (D.anchorAny && D.anchorAny.i >= from) {
        parts.push('<circle class="it-anchor" cx="' + xs(D.anchorAny.i).toFixed(1) + '" cy="' + ys(D.anchorAny.hCm).toFixed(1) + '" r="5"><title>' + esc(t('anchor_dot', { v: fmtLen(D.anchorAny.hCm) })) + '</title></circle>');
    }
    // Month-day ticks.
    const step = Math.max(1, Math.round((to - from) / (W < 520 ? 4 : 6)));
    for (let i = from; i <= to; i += step) {
        const d = new Date(D.days[i].d + 'T12:00:00Z');
        const lab = new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(d);
        parts.push('<text class="it-xl" x="' + xs(i).toFixed(1) + '" y="' + (H - 8) + '">' + esc(lab) + '</text>');
    }
    $('#itChart').innerHTML = '<svg viewBox="0 0 ' + W + ' ' + H + '" role="img" aria-label="' + esc(t('chart_label')) + '">' + parts.join('') + '</svg>';
    $('#itLegend').innerHTML = '<span><i class="lg-l"></i>' + esc(t('legend_model')) + '</span><span><i class="lg-b"></i>' + esc(t('legend_band')) + '</span>'
        + '<span><i class="lg-t"></i>' + esc(t('legend_thr', { src: t(THRESHOLDS.src) })) + '</span><span><i class="lg-f"></i>' + esc(t('legend_fc')) + '</span>';
}

function renderNotes() {
    const D = state.derived, s = D.today;
    const notes = [];
    if (state.water === 'river') notes.push(t('note_river'));
    if (s && s.frozen) {
        if (D.ti - D.fu <= 14) notes.push(t('note_first_ice'));
        if (s.thaw >= 5) notes.push(t('note_thaw', { v: nf(0).format(s.thaw) }));
        if (s.hs >= 0.08) notes.push(t('note_snow', { v: fmtLen(s.hs * 100) }));
    }
    const m = new Date(Date.now()).getUTCMonth() + 1;
    const north = state.loc.lat >= 0;
    if (s && s.frozen && ((north && m >= 3 && m <= 5) || (!north && m >= 9 && m <= 11))) notes.push(t('note_spring'));
    if (!state.data.fc || state.data.fc.err) notes.push(t('note_no_fc'));
    $('#itNotes').innerHTML = notes.map((n) => '<p class="tc-msg warn">' + esc(n) + '</p>').join('');
    $('#itAnchorNote').textContent = state.anchor
        ? t('anchor_on', { v: fmtLen(state.anchor.cm), date: fmtDate(state.anchor.d) })
        : t('anchor_off');
    $('#itAnchorClear').hidden = !state.anchor;
}

/* ── events ────────────────────────────────────────────────────────────── */
function bind() {
    $('#itChange').addEventListener('click', () => togglePicker($('#itPickerWrap').hidden));
    $('#itGeo').addEventListener('click', () => geolocate($('#itGeo'), (loc) => { togglePicker(false); load(loc); }));
    $('#itWater').addEventListener('click', (e) => {
        const b = e.target.closest('[data-water]');
        if (!b || b.dataset.water === state.water) return;
        state.water = b.dataset.water;
        syncWater(); savePrefs();
        if (state.data) { recompute(); render(); }
    });
    $('#itUnit').value = state.unit;
    $('#itUnit').addEventListener('change', () => {
        state.unit = $('#itUnit').value; savePrefs();
        syncAnchorUnit();
        if (state.data) render();
    });
    $('#itAnchorForm').addEventListener('submit', (e) => {
        e.preventDefault();
        const raw = parseFloat(String($('#itAnchorV').value).replace(',', '.'));
        const d = $('#itAnchorD').value;
        if (!Number.isFinite(raw) || raw < 0 || raw > (state.unit === 'in' ? 80 : 200) || !/^\d{4}-\d{2}-\d{2}$/.test(d)) {
            if (window.__toast) window.__toast(t('anchor_bad'), 'error');
            return;
        }
        state.anchor = { d, cm: state.unit === 'in' ? raw * 2.54 : raw };
        savePrefs();
        if (window.__aeFeature) window.__aeFeature('ice_anchor', { cm: Math.round(state.anchor.cm) });
        if (state.data) { recompute(); render(); }
    });
    $('#itAnchorClear').addEventListener('click', () => {
        state.anchor = null; savePrefs();
        if (state.data) { recompute(); render(); }
    });
}

function syncWater() {
    $('#itWater').querySelectorAll('[data-water]').forEach((b) => {
        const on = b.dataset.water === state.water;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', String(on));
    });
    $('#itWaterHint').textContent = t('water_hint_' + state.water);
}
function syncAnchorUnit() {
    $('#itAnchorUnit').textContent = state.unit === 'in' ? t('u_in') : t('u_cm');
}

function watchWidth() {
    if (!('ResizeObserver' in window)) return;
    let last = 0, timer = 0;
    new ResizeObserver((es) => {
        const w = Math.round(es[0].contentRect.width);
        if (!w || Math.abs(w - last) < 8) return;
        last = w;
        clearTimeout(timer);
        timer = setTimeout(() => { if (state.derived) renderChart(); }, 120);
    }).observe($('#itChart'));
}

function boot() {
    loadPrefs();
    watchWidth();
    syncWater();
    syncAnchorUnit();
    const pad = (n) => String(n).padStart(2, '0');
    const now = new Date();
    $('#itAnchorD').value = now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate());
    $('#itAnchorD').max = $('#itAnchorD').value;
    bind();
    window.__IT_APP = { state, reload: () => state.loc && load(state.loc) };
    const loc = fromUrl() || saved(SLUG) || fromProfile();
    if (loc) load(loc);
    else { status('empty', t('empty')); togglePicker(true); }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
